/**
 * The audience-size gauge's horizontal scale.
 *
 * The track is labelled with absolute matched-user counts — 10K (too narrow)
 * through 5M+ (too broad) — but the fill used to be driven by
 * `size / total_reach * 100`, the share of the reachable universe. Those are
 * unrelated quantities that happen to share the units `%`, so the bar and the
 * words under it were describing different things: a 998K audience against a
 * ~1M universe filled the bar to 99.8% and parked it on "5M+ (too broad)",
 * when 998K is barely past the sweet spot on the labelled scale.
 *
 * The share-of-reach figure is still correct and still shown — as the "≈ x% of
 * total reach" readout beside the count, where it means something.
 */

/**
 * Label breakpoints, in matched users, and the position each occupies on the
 * track. These MUST stay in step with the `.gauge-marks` labels in
 * `screens/audience-size-gauge.tsx`; the guard test at the bottom of the spec file checks
 * that every breakpoint below is still named there.
 */
export const AUDIENCE_GAUGE_STOPS: readonly { users: number; position: number }[] = [
  { users: 10_000, position: 0 },
  { users: 200_000, position: 25 },
  { users: 500_000, position: 50 },
  { users: 2_000_000, position: 75 },
  { users: 5_000_000, position: 100 },
];

/**
 * Where a cohort of `size` users sits on the labelled track, as a percentage.
 *
 * Piecewise-linear between the stops, because the stops are drawn evenly spaced
 * while the values between them are not (10K→200K is 20×, 200K→500K is 2.5×).
 * Interpolating within each segment is what makes the fill land where the eye
 * expects, given where the labels are printed.
 *
 * Below the first stop reads as 0 and above the last as 100 — "too narrow" and
 * "too broad" are the ends of the scale, not points on it.
 */
export function audienceGaugeFill(size: number | null | undefined): number {
  if (typeof size !== 'number' || !Number.isFinite(size) || size <= 0) return 0;

  const first = AUDIENCE_GAUGE_STOPS[0]!;
  const last = AUDIENCE_GAUGE_STOPS[AUDIENCE_GAUGE_STOPS.length - 1]!;
  if (size <= first.users) return first.position;
  if (size >= last.users) return last.position;

  for (let index = 1; index < AUDIENCE_GAUGE_STOPS.length; index += 1) {
    const upper = AUDIENCE_GAUGE_STOPS[index]!;
    if (size > upper.users) continue;
    const lower = AUDIENCE_GAUGE_STOPS[index - 1]!;
    const withinSegment = (size - lower.users) / (upper.users - lower.users);
    return lower.position + withinSegment * (upper.position - lower.position);
  }
  return last.position;
}
